import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  StyleSheet,
  View,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
  LayoutChangeEvent,
} from "react-native";
import { Image } from "expo-image";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";

/**
 * Props for the LazyMediaImage component.
 */
export interface ILazyMediaImageProps {
  /** The primary image resource URL */
  url: string;
  /** Optional fallback URL if the primary fails to load */
  fallbackUrl?: string;
  /** Media item title/name for accessibility */
  name?: string;
  /** Custom styles applied to image container */
  style?: any;
  /** Content fit mode: cover or contain */
  resizeMode?: "cover" | "contain";
  /** Whether the image is a compact thumbnail (<= 40px) */
  isCompactOrThumb?: boolean;
  /** Optional callback fired when image successfully finishes loading */
  onLoad?: () => void;
  /** Optional callback fired when image fails to load */
  onError?: () => void;
}

/**
 * Asynchronous, lazy-loading image component for media lists.
 * - Defers image network requests until the container scrolls into view.
 * - Displays a clear "Loading..." message in the image container while loading.
 * - Employs IntersectionObserver on web and viewport measurement on native.
 *
 * @param {ILazyMediaImageProps} props - Component properties.
 * @returns {React.JSX.Element} The rendered lazy image container.
 */
export function LazyMediaImage({
  url,
  fallbackUrl,
  name,
  style,
  resizeMode = "cover",
  isCompactOrThumb = false,
  onLoad,
  onError,
}: ILazyMediaImageProps): React.JSX.Element {
  const [activeUrl, setActiveUrl] = useState<string>(() => (url ? url.trim() : ""));
  const [isInView, setIsInView] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const containerRef = useRef<View>(null);
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();

  // Reset state when target URL changes
  useEffect(() => {
    const clean = url ? url.trim() : "";
    setActiveUrl(clean);
    setIsLoaded(false);
    setHasError(false);
  }, [url]);

  // Web: IntersectionObserver for true viewport-driven lazy loading
  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setIsInView(true);
      return;
    }

    if (isInView) {
      return;
    }

    const node = containerRef.current as unknown as HTMLElement | null;
    if (!node) {
      // If node is not immediately available, mark in view on next frame as fallback
      const timer = setTimeout(() => setIsInView(true), 100);
      return () => clearTimeout(timer);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry && (entry.isIntersecting || entry.intersectionRatio > 0)) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: "150px", // Preload slightly before appearing in viewport for smooth scrolling
        threshold: 0.01,
      }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [isInView]);

  // Native: measure position on layout to determine visibility
  const handleLayout = useCallback(
    (_event: LayoutChangeEvent) => {
      if (Platform.OS === "web" || isInView) {
        return;
      }

      if (!containerRef.current) {
        setIsInView(true);
        return;
      }

      containerRef.current.measureInWindow((x, y, width, height) => {
        // If within window bounds (with 200px threshold margin)
        if (
          y < windowHeight + 200 &&
          y + height > -200 &&
          x < windowWidth + 200 &&
          x + width > -200
        ) {
          setIsInView(true);
        }
      });
    },
    [isInView, windowHeight, windowWidth]
  );

  // Fallback timer for native: ensure images are not permanently blocked if measure fails
  useEffect(() => {
    if (Platform.OS === "web" || isInView) {
      return;
    }

    const timer = setTimeout(() => {
      setIsInView(true);
    }, 400);

    return () => clearTimeout(timer);
  }, [isInView]);

  const handleImageLoad = () => {
    setIsLoaded(true);
    if (onLoad) {
      onLoad();
    }
  };

  const handleImageError = () => {
    if (fallbackUrl && fallbackUrl.trim() && activeUrl !== fallbackUrl.trim()) {
      setActiveUrl(fallbackUrl.trim());
      setIsLoaded(false);
    } else {
      setHasError(true);
      setIsLoaded(true);
      if (onError) {
        onError();
      }
    }
  };

  const cleanUrl = activeUrl ? activeUrl.trim() : "";
  const sanitizedUrl = cleanUrl.includes(" ") ? encodeURI(cleanUrl) : cleanUrl;

  const flattenedStyle = StyleSheet.flatten(style) || {};
  const isMini =
    isCompactOrThumb ||
    (flattenedStyle.height != null && typeof flattenedStyle.height === "number" && flattenedStyle.height <= 40) ||
    (flattenedStyle.width != null && typeof flattenedStyle.width === "number" && flattenedStyle.width <= 40);

  // Error / missing URL state
  if (hasError || !sanitizedUrl) {
    return (
      <View
        ref={containerRef}
        style={[style, styles.placeholderContainer]}
        accessibilityLabel={name ? `${name} unavailable` : "Image unavailable"}
      >
        <Ionicons name="image-outline" size={isMini ? 16 : 32} color="#94A3B8" />
        {!isMini && (
          <ThemedText style={styles.errorText}>Image unavailable</ThemedText>
        )}
      </View>
    );
  }

  return (
    <View
      ref={containerRef}
      style={[style, styles.wrapper]}
      onLayout={handleLayout}
      accessibilityLabel={name ? `Image for ${name}` : "Media image"}
    >
      {/* Asynchronous Expo Image: only requested once container enters viewport */}
      {isInView && (
        <Image
          key={sanitizedUrl}
          recyclingKey={sanitizedUrl}
          source={{ uri: sanitizedUrl }}
          style={StyleSheet.absoluteFill}
          contentFit={resizeMode === "contain" ? "contain" : "cover"}
          transition={Platform.OS === "android" ? 0 : 150}
          cachePolicy="memory-disk"
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}

      {/* Loading message displayed in the container until the image is loaded */}
      {!isLoaded && !hasError && (
        <View style={[StyleSheet.absoluteFill, styles.loadingOverlay]}>
          <ActivityIndicator size="small" color="#4A90E2" style={{ marginBottom: isMini ? 0 : 4 }} />
          {!isMini && (
            <ThemedText style={styles.loadingText}>Loading...</ThemedText>
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: "relative",
    overflow: "hidden",
    backgroundColor: "rgba(148, 163, 184, 0.08)",
  },
  placeholderContainer: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(148, 163, 184, 0.08)",
    gap: 4,
  },
  loadingOverlay: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(148, 163, 184, 0.12)",
    zIndex: 2,
    padding: 4,
  },
  loadingText: {
    fontSize: 10,
    fontWeight: "600",
    color: "#64748B",
    letterSpacing: 0.2,
  },
  errorText: {
    fontSize: 10,
    fontWeight: "bold",
    opacity: 0.6,
    color: "#64748B",
  },
});
